export type RecommendedCourse = {
  id: string;
  title: string;
  domain: string;
  recommendations: number;
  /** Share of users who received this recommendation and then clicked or started it. */
  clickedShare?: number;
  startedShare?: number;
};

/** Demo figures for Basisschool De Linde. Course names match the starter-teacher catalogue. */
export const recommendedCourses: readonly RecommendedCourse[] = [
  {
    id: "lesstart",
    title: "Een rustige lesstart, oefenles 1",
    domain: "Klassenmanagement",
    recommendations: 18,
    clickedShare: 83,
    startedShare: 58,
  },
  {
    id: "onrust",
    title: "Omgaan met onrust, oefenles 1",
    domain: "Pedagogisch handelen",
    recommendations: 15,
    clickedShare: 67,
    startedShare: 50,
  },
  {
    id: "oudergesprek",
    title: "Eerste oudergesprek, oefenles 1",
    domain: "Pedagogisch handelen",
    recommendations: 13,
    clickedShare: 64,
    startedShare: 45,
  },
  {
    id: "grenzen",
    title: "Grenzen stellen in de groep, oefenles 1",
    domain: "Pedagogisch handelen",
    recommendations: 11,
    clickedShare: 80,
    startedShare: 50,
  },
  {
    id: "feedback",
    title: "Feedback geven aan leerlingen, oefenles 1",
    domain: "Pedagogisch handelen",
    recommendations: 9,
    clickedShare: 67,
    startedShare: 56,
  },
  {
    id: "instructie",
    title: "Instructie geven, oefenles 1",
    domain: "Didactisch handelen",
    recommendations: 8,
  },
  {
    id: "ouders",
    title: "Gesprek met ouders, oefenles 1",
    domain: "Communicatie op school",
    recommendations: 7,
  },
  {
    id: "vastloopt",
    title: "Een leerling die vastloopt, oefenles 1",
    domain: "Leerlingbegeleiding",
    recommendations: 6,
  },
  {
    id: "groepsvorming",
    title: "Groepsvorming en klassenafspraken, oefenles 1",
    domain: "Pedagogisch handelen",
    recommendations: 5,
  },
  {
    id: "differentiatie",
    title: "Differentiatie in de les, oefenles 1",
    domain: "Didactisch handelen",
    recommendations: 3,
  },
  {
    id: "digitaal",
    title: "Digitale middelen in de les, oefenles 1",
    domain: "Professionele groei",
    recommendations: 2,
  },
  {
    id: "begeleiding",
    title: "Begeleiding vragen, oefenles 1",
    domain: "Professionele groei",
    recommendations: 1,
  },
];

export function rankedCourses(): RecommendedCourse[] {
  return [...recommendedCourses].sort((left, right) => right.recommendations - left.recommendations);
}

export function topRecommendedCourses(limit = 5): RecommendedCourse[] {
  return rankedCourses().slice(0, limit);
}

export type CourseFollowThrough = RecommendedCourse & {
  recommendedShare: number;
  clickedShare: number;
  startedShare: number;
};

/** Top five courses: share of all recommendations, then the share of recipients who clicked and started. */
export function topCourseFollowThrough(): CourseFollowThrough[] {
  return topRecommendedCourses(5).flatMap((course) =>
    course.clickedShare === undefined || course.startedShare === undefined
      ? []
      : [
          {
            ...course,
            recommendedShare: recommendationShare(course.recommendations),
            clickedShare: course.clickedShare,
            startedShare: course.startedShare,
          },
        ],
  );
}

export function recommendationTotal(): number {
  return recommendedCourses.reduce((total, course) => total + course.recommendations, 0);
}

/** Share of all recommendations. A course total, never tied to a person. */
export function recommendationShare(recommendations: number): number {
  const total = recommendationTotal();
  if (total <= 0) return 0;
  return Math.round((recommendations / total) * 100);
}

export function leastRecommendedCourses(limit = 5): RecommendedCourse[] {
  return rankedCourses().slice(-limit);
}
